/**
 * Password Recovery Page - UPDATED 10/28/25
 * 
 * CHANGES MADE ON 10/28/25:
 * - Removed "Show Token" functionality and tab system entirely
 * - Streamlined to single "Instant Reset" option only
 * - Removed all token-related code, variables, and UI components
 * - Simplified user experience to direct password reset form
 * - Footer text already correctly shows "Email us at hello@bravethingslab.com"
 * 
 * Provides instant password reset without email requirement.
 * Users can directly set a new password by providing their email address
 * and entering a new password, which is immediately applied to their account.
 * 
 * This page offers a practical alternative to email-based password reset
 * that works perfectly in demo/development environments without email setup.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

import { Loader2, KeyRound, ArrowLeft, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { client } from "@/lib/api";

export default function PasswordRecovery() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Account reset form
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Handle instant account reset
  const handleInstantReset = async () => {
    if (!resetEmail || !newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Call the instant reset API endpoint
      const response = await client.api.auth["instant-reset"].$post({
        json: {
          email: resetEmail,
          newPassword: newPassword
        }
      });

      // Parse response JSON first
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Success case - data has 'message' property
        toast.success("Password reset successfully!");
        
        // Navigate to login page with success message
        navigate('/login', {
          state: {
            message: `Password reset complete for ${resetEmail}. Please log in with your new password.`
          }
        });
      } else {
        // Error case - check if data has error property or use fallback
        if (data.success === false && 'error' in data) {
          setError(data.error);
        } else {
          setError("Failed to reset password. Please try again.");
        }
      }
    } catch (error) {
      console.error("Instant reset error:", error);
      setError("Failed to reset account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-forest-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-2xl"
      >
        {/* Back to Login Link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-6"
        >
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-forest-600 hover:text-forest-700 transition-colors duration-200 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </motion.div>

        <Card className="border-2 border-forest-100 shadow-xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center space-y-4">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mx-auto w-16 h-16 bg-forest-100 rounded-full flex items-center justify-center"
            >
              <KeyRound className="w-8 h-8 text-forest-600" />
            </motion.div>

            <div>
              <CardTitle className="text-2xl font-bold text-forest-800">
                Password Recovery
              </CardTitle>
              <CardDescription className="text-gray-600 mt-2">
                Choose a recovery method - no email required!
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            {/* Removed Tabs component - now just direct form */}
            <div className="space-y-6">
              <Alert className="border-golden-200 bg-golden-50">
                <Lightbulb className="w-4 h-4 text-golden-600" />
                <AlertDescription className="text-golden-700">
                  <strong>Instant Reset:</strong> Set a new password immediately.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-forest-700 font-medium">
                    Your Email Address
                  </Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    disabled={isLoading}
                    className="border-forest-200 focus:border-forest-500 focus:ring-forest-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-forest-700 font-medium">
                    New Password
                  </Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter your new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                    className="border-forest-200 focus:border-forest-500 focus:ring-forest-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-forest-700 font-medium">
                    Confirm New Password
                  </Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    className="border-forest-200 focus:border-forest-500 focus:ring-forest-500"
                  />
                </div>

                {error && (
                  <Alert className="border-coral-200 bg-coral-50">
                    <AlertDescription className="text-coral-700">
                      {error}
                    </AlertDescription>
                  </Alert>
                )}

                <Button
                  onClick={handleInstantReset}
                  disabled={isLoading}
                  className="w-full bg-golden-600 hover:bg-golden-700 text-white font-medium py-2.5"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resetting Password...
                    </>
                  ) : (
                    <>
                      <Lightbulb className="w-4 h-4 mr-2" />
                      Reset Password Now
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center space-y-2">
              <p className="text-sm text-gray-600">
                Still need help? Email us at{" "}
                <a
                  href="mailto:admin@bravethingslab.com"
                  className="text-forest-600 font-medium underline"
                >
                  admin@bravethingslab.com
                </a>
                {" "}for support
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
